import { useEffect, useId, useState } from 'react';
import { DateTime } from 'luxon';
import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import { getTemplates } from 'actions/templates';
import TrainingList from 'components/Trainings/TrainingList';
import PageLayout from 'components/PageLayout';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { Button, DatePicker, Search } from '@teachstonellc/component-library';
import { Link } from 'react-router-dom';
import StatusFilter from 'components/Trainings/StatusFilter';
import Permission from 'components/Subscription/Permission';
import { useQueryState, useQueryStringUpdate } from './utils';
import { TrainingTemplate } from 'types/api/TrainingTemplate';
import { TRAINING_STATUS } from 'types/api/Training';

function Home() {
  useDocumentTitle(i18n.ft(messages.trainings));

  const trainingsStatusA11yId = useId();
  const formatDateForFilters = (
    date: Date | string | null | undefined
  ): string => {
    if (!date) {
      return '';
    }

    if (typeof date === 'string') {
      return date;
    }

    return DateTime.fromJSDate(date).toFormat('MM/dd/yyyy');
  };

  const convertStringToDate = (dateString?: string): Date | undefined => {
    if (!dateString) return undefined;

    const parsed = DateTime.fromFormat(dateString, 'MM/dd/yyyy');

    return parsed.isValid ? parsed.toJSDate() : undefined;
  };

  const [templates, setTemplates] = useState<TrainingTemplate[]>([]);
  const [query, setQuery] = useQueryState('query');
  const [status, setStatus] = useQueryState('status');
  const [startDate, setStartDate] = useQueryState('startDate');
  const [endDate, setEndDate] = useQueryState('endDate');
  useQueryStringUpdate({ query, status, startDate, endDate });

  useEffect(() => {
    getTemplates({ per_page: 10000 }).then(({ data }) => {
      setTemplates(data.training_templates);
    });
  }, []);

  const endDateValue = convertStringToDate(endDate);
  const startDateValue = convertStringToDate(startDate);

  return (
    <PageLayout>
      <div className="flex flex-col items-start justify-between sm:flex-row">
        <div className="mb-4">
          <h1 className="text-ds-2xl text-ds-black">
            {i18n.ft(messages.trainings)}
          </h1>
          <h2 className="font-normal text-ds-md text-ds-text-subtle">
            {i18n.ft(messages.selectTraining)}
          </h2>
        </div>

        <Permission
          zone="trainer_dashboard"
          resource="trainings"
          operation="create"
          or={templates.length > 0}
        >
          <Link to={RouteHelpers.getPath('td-catalog')}>
            <Button shape="rounded" className="mb-4 w-full sm:mb-0 sm:w-auto">
              {i18n.ft(messages.createTraining)}
            </Button>
          </Link>
        </Permission>
      </div>

      <StatusFilter
        value={status}
        onChange={setStatus}
        a11yId={trainingsStatusA11yId}
      />

      <div className="mb-6 flex gap-4 justify-start flex-wrap sm:flex-nowrap sm:justify-between">
        <div className="w-full mb-4 sm:flex-1 sm:mb-0">
          <Search
            label={i18n.ft(messages.search)}
            id="search"
            placeholder={i18n.ft(messages.searchBy)}
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setQuery(e.target.value)
            }
          />
        </div>

        <div className="mb-4 mr-4 sm:mb-0 sm:mr-0">
          <DatePicker
            label={i18n.ft(messages.startDate)}
            placeholder={i18n.ft(messages.anyDate)}
            value={startDateValue}
            onChange={(date: Date | undefined) => {
              const nextStartDate = formatDateForFilters(date);

              if (!nextStartDate || nextStartDate === startDate) {
                setStartDate('');
                return;
              }

              setStartDate(nextStartDate);
            }}
            disabled={endDateValue ? { after: endDateValue } : undefined}
          />
        </div>

        <div>
          <DatePicker
            label={i18n.ft(messages.endDate)}
            placeholder={i18n.ft(messages.anyDate)}
            value={endDateValue}
            onChange={(date: Date | undefined) => {
              const nextEndDate = formatDateForFilters(date);

              if (!nextEndDate || nextEndDate === endDate) {
                setEndDate('');
                return;
              }

              setEndDate(nextEndDate);
            }}
            disabled={startDateValue ? { before: startDateValue } : undefined}
          />
        </div>
      </div>

      <TrainingList
        status={status === '' ? undefined : (status as TRAINING_STATUS)}
        query={query}
        startDate={startDate}
        endDate={endDate}
        a11yId={trainingsStatusA11yId}
      />
    </PageLayout>
  );
}

export default Home;
